import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useLocationStore } from '@/stores/location'
import { useBusinessStore } from '@/stores/business'
import { storeOrderService } from '@/services'

const POLL_VISIBLE_MS = 10000
const POLL_HIDDEN_MS = 60000
const HISTORY_DEFAULT_PER_PAGE = 25

export const useStoreOrderQueueStore = defineStore('storeOrderQueue', () => {
  const sales = ref([])
  const loading = ref(false)
  const error = ref('')
  const queueView = ref('active')
  const historySearch = ref('')
  const historyPagination = ref({
    page: 1,
    rowsPerPage: HISTORY_DEFAULT_PER_PAGE,
    rowsNumber: 0
  })

  let pollTimer = null
  let subscriberCount = 0
  let locationWatchStop = null

  function clearPoll () {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function schedulePoll () {
    clearPoll()
    if (subscriberCount <= 0) return
    if (queueView.value === 'history') return
    const ms = typeof document !== 'undefined' && document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS
    pollTimer = window.setInterval(() => {
      fetchQueue()
    }, ms)
  }

  function onVisibilityChange () {
    schedulePoll()
    if (typeof document !== 'undefined' && !document.hidden) {
      fetchQueue()
    }
  }

  async function fetchQueue (options = {}) {
    const businessStore = useBusinessStore()
    if (!businessStore.info?.store_enabled) {
      sales.value = []
      error.value = ''
      loading.value = false
      return
    }

    const locationStore = useLocationStore()
    const locId = locationStore.getCurrentLocation?.id
    if (!locId) {
      sales.value = []
      return
    }
    loading.value = true
    error.value = ''
    const isHistory = queueView.value === 'history'
    try {
      const data = await storeOrderService.fetchStoreOrderQueue(locId, {
        activeOnly: !isHistory,
        page: isHistory ? (options.page ?? historyPagination.value.page) : undefined,
        perPage: isHistory ? (options.perPage ?? historyPagination.value.rowsPerPage) : undefined,
        q: isHistory ? (options.q ?? historySearch.value) : undefined
      })
      sales.value = data.sales || []
      if (isHistory && data.meta) {
        historyPagination.value = {
          ...historyPagination.value,
          page: data.meta.current_page ?? historyPagination.value.page,
          rowsPerPage: data.meta.per_page ?? historyPagination.value.rowsPerPage,
          rowsNumber: data.meta.total ?? 0
        }
      }
    } catch (e) {
      error.value = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Failed to load queue'
      sales.value = []
    } finally {
      loading.value = false
    }
  }

  async function patchFulfillment (saleId, fulfillmentStatus, extras = {}) {
    await storeOrderService.updateStoreOrderFulfillment(saleId, fulfillmentStatus, extras)
  }

  async function markReturned (saleId, note) {
    await storeOrderService.markStoreOrderReturned(saleId, note)
  }

  function setQueueView (view) {
    const next = view === 'history' ? 'history' : 'active'
    if (queueView.value === next) return
    queueView.value = next
    if (next === 'history') {
      historyPagination.value.page = 1
      clearPoll()
    } else {
      schedulePoll()
    }
    fetchQueue()
  }

  function setHistorySearch (q) {
    historySearch.value = q
    historyPagination.value.page = 1
    fetchQueue({ q, page: 1 })
  }

  function setHistoryPage (page, rowsPerPage) {
    historyPagination.value.page = page
    if (rowsPerPage != null) {
      historyPagination.value.rowsPerPage = rowsPerPage
    }
    fetchQueue({ page, perPage: historyPagination.value.rowsPerPage })
  }

  const queueCount = computed(() => sales.value.length)

  function subscribe () {
    subscriberCount++
    if (subscriberCount === 1) {
      fetchQueue()
      schedulePoll()
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange)
      }
      const locationStore = useLocationStore()
      locationWatchStop = watch(
        () => locationStore.getCurrentLocation?.id,
        () => fetchQueue()
      )
    }
  }

  function unsubscribe () {
    subscriberCount = Math.max(0, subscriberCount - 1)
    if (subscriberCount === 0) {
      clearPoll()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }
      if (locationWatchStop) {
        locationWatchStop()
        locationWatchStop = null
      }
    }
  }

  return {
    sales,
    loading,
    error,
    queueCount,
    queueView,
    historySearch,
    historyPagination,
    setQueueView,
    setHistorySearch,
    setHistoryPage,
    fetchQueue,
    patchFulfillment,
    markReturned,
    subscribe,
    unsubscribe
  }
})
